export interface OTIItem {
  dCodOTI: string; // Código OTI (01-09)
  dValOTI: string; // Valor del OTI
}

export interface InvoiceItem {
  id: string;
  productId: string;
  reference: string; // Referencia
  productCode: string; // Código
  productName: string;
  description: string; // Descripción
  quantity: number; // Cant
  unit: string; // Unidad de medida (und, Hora, kg, etc.)
  unitPrice: number; // Precio unit.
  unitDiscount: number; // Descuento unit. (en valor, no porcentaje)
  discountPercent: number; // Porcentaje de descuento (para compatibilidad)
  taxRate: number; // ITBMS (tasa %)
  taxAmount: number; // ITBMS calculado
  iscRate: number; // ISC (Tasa) - Impuesto Selectivo al Consumo
  iscAmount: number; // ISC calculado
  otiAmount: number; // OTI (Otros tributos indirectos) - DEPRECATED, usar gOTIItem
  gOTIItem?: OTIItem[]; // Array de OTI por item
  shippingAmount: number; // Acarreo por item (dPrAcarItem)
  insuranceAmount: number; // Seguro por item (dPrSegItem)
  segment?: string; // UNSPSC Segment code
  family?: string; // UNSPSC Family code
  // Información de Pedido/Orden por Item (gPedComIr)
  orderNumber?: string; // Número de pedido (dNroPed)
  orderItemSequence?: string; // Secuencia del item en el pedido (dSecItemPed)
  orderItemInfo?: string; // Información adicional del pedido (dInfEmPedIt)
  subtotal: number;
  total: number;
}

export interface InvoiceWizardData {
  // Document Mode - Tipo de documento a crear
  documentMode: 'invoice' | 'credit-note' | 'debit-note'; // Modo del documento
  referencedInvoiceId?: string; // ID de la factura referenciada (solo para notas)

  // Correction Mode - Para corregir facturas con error
  isFixingEBill?: boolean; // true = corregir manteniendo mismo CUFE, false = crear nueva
  onFixingEBillId?: string; // ID de la factura a corregir

  // Datos originales de la factura (necesarios para corrección con mismo CUFE)
  originalCufe?: string; // CUFE original (dId)
  originalSeg?: string; // Segmento original (dSeg)
  originalInvoiceNumber?: string; // Número de factura original (dNroDF)
  originalBillingPointCode?: string; // Punto de facturación original (dPtoFacDF)
  originalBranchOfficeCode?: string; // Sucursal original (dSucEm)
  originalEmissionDate?: string; // Fecha emisión original (dFechaEm)

  // gDFRef - Referencia a Documento Fiscal (solo para Notas de Crédito/Débito)
  gDFRef?: {
    dNroFacIE: string | null; // Número de factura interna electrónica
    dNroFacPap: string | null; // Número de factura en papel
    // Los tres identificadores son mutuamente excluyentes según el tipo de
    // referencia elegido, así que cualquiera puede venir nulo. El CUFE se guarda
    // SIN el prefijo `FE`; ver utils/cufe.ts.
    dCufeRef: string | null; // CUFE de la factura referenciada
    dFechaDFRef: string | null; // Fecha del documento referenciado (formato ISO)
  };

  // Step 1: Issuer (Emisor) Information - EXPANDIDO
  issuerName: string;
  issuerCode: string; // RUC del emisor
  issuerCompanyCode: string; // Company Code (ej: Hype040124)
  issuerDv: string; // DV del emisor
  issuerTypeTaxpayer: string; // Tipo de contribuyente del emisor (1=Natural, 2=Jurídico)
  issuerEmail: string; // Email del emisor
  issuerPhone: string; // Teléfono del emisor
  issuerImage?: string;

  // Branch Office Information - EXPANDIDO
  branchOfficeId: string;
  branchOfficeName: string;
  branchOfficeAddress: string; // Dirección de la sucursal
  branchOfficeAddressCode: string; // Address Code de la sucursal
  branchOfficeLatitude: string; // Latitud de la sucursal
  branchOfficeLongitude: string; // Longitud de la sucursal
  branchOfficeProvince: string; // Provincia de la sucursal
  branchOfficeDistrict: string; // Distrito de la sucursal
  branchOfficeTownship: string; // Corregimiento de la sucursal
  branchOfficeFiscalCode: string; // Código de ubicación fiscal de la sucursal
  branchOfficeCountryId: string; // CountryId de la sucursal
  branchOfficeCountryCode: string; // Código del país (ej: PA, US)
  branchOfficeCountryName: string; // Nombre del país (ej: PANAMÁ)
  billingPointCode: string;

  // Step 1: Receiver (Receptor) Information
  receiverType: string; // Tipo de receptor (iTipoRec): 01=Contribuyente, 02=Consumidor Final, 03=Gobierno, 04=Extranjero
  customerId: string;
  customerName: string; // Nombre del receptor
  customerRuc: string; // RUC/ID
  customerDv: string; // DV del receptor
  customerRucType: string; // Tipo de documento: 01=RUC, 02=CEDULA, 03=PASAPORTE, 04=OTRO
  customerRecepType: string; // Tipo de RUC (dTipoRuc): 1=Natural, 2=Jurídico
  customerEmail: string; // Correo electrónico del receptor
  customerPhone?: string; // Teléfono del receptor

  // Foreign Customer Information (for iTipoRec: "04")
  customerForeignId?: string; // ID extranjero (ej: 081-101182-0004L)
  customerForeignCountry?: string; // Código de país extranjero (ej: NI, CR, US)

  // Export Data (gFExp) - Solo cuando operationDestination === '2' (Extranjero)
  exportIncoterms?: string; // B501 cCondEntr - Código Incoterms, obligatorio (Ocu 1-1)
  exportCurrency?: string; // B502 cMoneda - Código de moneda, opcional (Ocu 0-1)
  exportCurrencyDescription?: string; // B503 cMonedaDesc - Obligatorio si B502=ZZZ
  exportExchangeRate?: number; // B504 dCambio - Obligatorio si existe B502
  exportPortOfEmbarkation?: string; // B506 dPuertoEmbarq - Opcional (Ocu 0-1), min 5 chars

  // Address Information
  customerAddress: string; // Descripción de la dirección
  customerCountry: string; // País
  customerProvince: string; // Provincia
  customerDistrict: string; // Distrito
  customerTownship: string; // Corregimiento
  customerAddressFiscalCode: string; // Código fiscal de ubicación
  customerCity: string;

  // Step 2: Invoice Details
  invoiceNumber: string; // Número de factura
  invoiceNumberMode: 'auto' | 'manual'; // Modo: automático o manual
  invoiceDate: string;
  dueDate: string;
  invoiceType: string; // Tipo de factura/documento
  documentType: string; // Tipo de documento (01-10)
  operationType: string; // Tipo de operación: 1=Salida/venta, 2=Entrada/compra
  containerShipping: string; // Envío del contenedor: 1=Normal, 2=Exceptuado
  operationDestination: string; // Destino: 1=Panamá, 2=Extranjero
  operationNature: string; // Naturaleza de la operación (01, 02, 10, etc.)
  documentFormatGeneration: string; // Generación de documento: 1=Sin CAFE, 2=Cinta, 3=Papel
  salesTransactionType: string; // Tipo de transacción de venta: 1=Giro del negocio, 2=Activo fijo, 3=Bienes raíces, 4=Prestación de servicio
  currency: string;

  // Step 3: Items
  items: InvoiceItem[];

  // Step 4: Additional Details
  notes: string;
  reference: string;
  retention: string; // Tipo de retención (0-8)
  retentionAmount: number; // Monto manual de retención (usado cuando código = 8)

  // Calculated Totals
  subtotal: number;
  totalTax: number; // Total ITBMS
  totalDiscount: number; // Suma de descuentos unitarios (a nivel de item)
  totalIsc: number; // Total ISC (Impuesto Selectivo al Consumo)
  totalOti: number; // Total OTI (Otros Tributos Indirectos) - DEPRECATED, usar gOTITotal
  gOTITotal?: Array<{
    dCodOTITotal: string; // Código OTI (01-09)
    dValOTITotal: string; // Valor total del OTI agrupado
  }>; // Array de totales OTI agrupados por código
  shippingCost: number; // Acarreos
  insuranceCost: number; // Seguros
  grandTotal: number;

  // Global Discounts (descuentos aplicados al total de la factura)
  globalDiscounts: Array<{
    description: string; // Descripción del descuento
    amount: number; // Valor del descuento
  }>;

  // Información de Pedido Global (gPedComGl)
  globalOrderNumber?: string; // Número de pedido global (dNroPed)
  globalOrderAcceptanceNumber?: string; // Número de aceptación (dNumAcept)
  globalOrderReceiverCode?: string; // Código del receptor (dCodRec)
  globalOrderIssuerSystemCode?: string; // Código del sistema emisor (dCodSisEm)
  globalOrderInfo?: string; // Información adicional del pedido global (dInfEmPedGl)

  // Payment Conditions (Step 4) - Condiciones de pago
  paymentCondition: string; // 1=Contado, 2=Crédito

  // Payment Details (Step 4) - Múltiples formas de pago
  payments: Array<{
    method: string; // Código de forma de pago (01=Efectivo, 02=Cheque, etc.)
    amount: number; // Monto de este pago
    description?: string; // Descripción opcional
  }>;

  // Payment Terms (gPagPlazo) - Información de pago a plazo (solo cuando paymentCondition != "1")
  paymentTerms?: Array<{
    dSecItem: string; // Secuencia del item de plazo (1, 2, 3, etc.)
    dFecItPlazo: string; // Fecha del plazo (formato ISO)
    dValItPlazo: string; // Valor de la cuota
    dInfPagPlazo: string; // Información adicional del plazo
  }>;

  // Legacy fields (deprecated, mantener por compatibilidad)
  paymentMethod: string;
  amountPaid: number; // Monto pagado por el cliente
  change: number; // Vuelto (calculado)
  balance: number; // Saldo pendiente (calculado)
}

export const INVOICE_WIZARD_INITIAL_STATE: InvoiceWizardData = {
  // Document Mode
  documentMode: 'invoice', // Por defecto es factura normal
  referencedInvoiceId: undefined,
  gDFRef: undefined,

  // Issuer
  issuerName: '',
  issuerCode: '',
  issuerCompanyCode: '',
  issuerDv: '',
  issuerTypeTaxpayer: '2', // Por defecto Jurídico
  issuerEmail: '',
  issuerPhone: '',
  issuerImage: '',

  // Branch Office
  branchOfficeId: '',
  branchOfficeName: '',
  branchOfficeAddress: '',
  branchOfficeAddressCode: '',
  branchOfficeLatitude: '',
  branchOfficeLongitude: '',
  branchOfficeProvince: '',
  branchOfficeDistrict: '',
  branchOfficeTownship: '',
  branchOfficeFiscalCode: '',
  branchOfficeCountryId: '',
  branchOfficeCountryCode: 'PA', // Por defecto Panamá
  branchOfficeCountryName: 'PANAMÁ', // Por defecto Panamá
  billingPointCode: '001',

  // Receiver
  receiverType: '01', // Contribuyente
  customerId: '',
  customerName: '',
  customerRuc: '',
  customerDv: '',
  customerRucType: '01', // RUC
  customerRecepType: '1', // Natural
  customerEmail: '',
  customerPhone: '',

  // Address
  customerAddress: '',
  customerCountry: '',
  customerProvince: '',
  customerDistrict: '',
  customerTownship: '',
  customerAddressFiscalCode: '',
  customerCity: '',

  // Export Data (gFExp) - valores por defecto para exportación
  exportIncoterms: undefined,
  exportCurrency: undefined,
  exportCurrencyDescription: undefined,
  exportExchangeRate: undefined,
  exportPortOfEmbarkation: undefined,

  // Invoice Details
  invoiceNumber: '',
  invoiceNumberMode: 'auto',
  invoiceDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
  dueDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
  invoiceType: 'FE', // Factura Electrónica
  documentType: '01', // Factura de Operación Interna
  operationType: '1', // Salida o venta
  containerShipping: '1', // Normal
  operationDestination: '1', // Panamá
  operationNature: '01', // Venta
  documentFormatGeneration: '1', // Sin generación de CAFE
  salesTransactionType: '1', // Venta de giro del negocio (por defecto)
  currency: 'USD',

  // Items & Additional
  items: [],
  notes: '',
  reference: '',
  retention: '0', // No aplica por defecto
  retentionAmount: 0,

  // Totals
  subtotal: 0,
  totalTax: 0,
  totalDiscount: 0,
  totalIsc: 0,
  totalOti: 0,
  shippingCost: 0,
  insuranceCost: 0,
  grandTotal: 0,

  // Global Discounts
  globalDiscounts: [],

  // Payment Condition
  paymentCondition: '1', // 1=Contado por defecto

  // Payments (múltiples formas de pago)
  payments: [],

  // Payment Terms (pago a plazo)
  paymentTerms: [],

  // Legacy payment fields
  paymentMethod: '02', // Efectivo por defecto
  amountPaid: 0,
  change: 0,
  balance: 0,
};
